import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useFormContext } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { IApprovalTemplate } from "../../../types/global";
import { IPurchaseReturn } from "../../../types/Purchase/purchaseReturn";

import {
  ActivityType,
  ActivityLogDocumentType,
} from "../../../generated/general";

import { useDisable } from "../../../hooks/use-disable";
import { useActivityLog } from "../../../hooks/use-activity-log";
import { usePurchaseOption } from "../../../hooks/Purchase/use-purchase-option";
import { useStateContext } from "../../../contexts/auth-context";

import { createGraphQLClientWithMiddleware } from "../../../services/graphqlClient";

import { Stack } from "@mui/material";
import LoadingUI from "../../../components/UI/LoadingUI";
import PurchaseFooter from "../../../components/Form/Purchase/Footer";
import VendorInfo from "../../../components/Form/Purchase/VendorInfo";
import BottomNavbar from "../../../components/UI/Navbar/BottomNavbar";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import AddressInfo from "../../../components/Form/Purchase/VendorInfo/AddressInfo";
import PurchaseReturnHeader from "../../../components/Form/Purchase/Return/Header";
import PurchaseReturnItemList from "../../../components/Table/Purchase/ItemList/PurchaseReturnItemList";

import ReturnInfo from "../../../components/Form/Purchase/ReturnInfo/ReturnInfo";

import {
  purchaseReturnQueryFormatter,
  purchaseReturnCreatePayloadFormatter,
  purchaseReturnUpdatePayloadFormatter,
} from "../../../utils/Formatter/Purchase/PurchaseReturn";
import { errorMessageFormatter } from "../../../utils/Global";
import {
  PurchaseDocumentType,
  PurchaseReturnCreateInput,
  PurchaseReturnFindUniqueQuery,
  PurchaseReturnUpdateInput,
  usePurchaseItemFindManyQuery,
  usePurchaseReturnCreateMutation,
  usePurchaseReturnFindUniqueQuery,
  usePurchaseReturnUpdateMutation,
} from "../../../generated/purchase";
import { IPurchaseItemList } from "../../../types/Purchase";
import { usePurchaseItemListFormatter } from "../../../hooks/Purchase/use-item-list-formatter";

const DocumentInfoTab = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { state } = useLocation();
  const navigate = useNavigate();
  const documentType = "purchase_return";
  const { enqueueSnackbar } = useSnackbar();
  const [disabled, setDisabled] = useDisable();
  const { createActivityLog } = useActivityLog();
  const [approvalStep, setApprovalStep] = useState<number>(0);
  const [isLoadingData, setIsLoadingData] = useState<boolean>(true);
  const [isHaveApproval, setIsHaveApproval] = useState<boolean>(false);
  const [approverList, setApproverList] = useState<IApprovalTemplate[]>([]);
  const { isLoading: isOptionLoading, approvalTemplates } =
    usePurchaseOption(documentType);
  const [referenceItemList, setReferenceItemList] = useState<
    IPurchaseItemList[]
  >([]);

  const isApproval = approvalTemplates && approvalTemplates.length > 0;

  const {
    state: { authUser },
  } = useStateContext();

  const graphQLClient = createGraphQLClientWithMiddleware("purchase");

  const {
    getValues,
    reset,
    handleSubmit,
    watch,
    formState: { dirtyFields },
  } = useFormContext<IPurchaseReturn>();

  const status = watch("aggrid_status");
  const [netAmount, setNetAmount] = useState<number | null | undefined>(0);

  const { data, isLoading, isSuccess, refetch } =
    usePurchaseReturnFindUniqueQuery<PurchaseReturnFindUniqueQuery>(
      graphQLClient,
      {
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
      },
      {
        enabled: !!id,
        cacheTime: 0,
      }
    );

  const { data: purchaseItemList, isLoading: itemListLoading } =
    usePurchaseItemFindManyQuery(
      graphQLClient,
      {
        input: {
          reference_document_type: PurchaseDocumentType.PurchaseOrder,
          reference_unique_id:
            data?.PurchaseReturnFindUnique?.reference_document_list &&
            data?.PurchaseReturnFindUnique?.reference_document_list[0]
              .document_unique_id,
        },
      },
      {
        enabled: Boolean(
          data?.PurchaseReturnFindUnique?.reference_document_list &&
          data?.PurchaseReturnFindUnique?.reference_document_list[0]
            .document_unique_id
        ),
      }
    );

  const formatItemList = usePurchaseItemListFormatter(
    purchaseItemList?.PurchaseItemFindMany as IPurchaseItemList[]
  );

  useEffect(() => {
    setIsLoadingData(true);
    if (isSuccess) {
      const { PurchaseReturnFindUnique } = data;
      setNetAmount(PurchaseReturnFindUnique.net_amount);
      const getPurchaseReturnData = async () => {
        const formattedPurchaseReturn = await purchaseReturnQueryFormatter(
          PurchaseReturnFindUnique
        );
        reset(formattedPurchaseReturn);
        setIsLoadingData(false);
      };
      getPurchaseReturnData();
    }
  }, [data, isSuccess, reset]);

  useEffect(() => {
    if (isSuccess) {
      const { PurchaseReturnFindUnique } = data;
      const currentStep = PurchaseReturnFindUnique?.approval_step as number;
      setApprovalStep(currentStep);
      if (PurchaseReturnFindUnique?.main_status === "wait_approve") {
        setApproverList(approvalTemplates as IApprovalTemplate[]);
      } else {
        const allApproval = PurchaseReturnFindUnique?.approver_list || [];
        setApproverList(allApproval);
      }

      const currentReviewerList =
        approvalTemplates.find(
          (template) => template?.step_number === currentStep
        )?.approver_list || [];

      const isHaveApproval = currentReviewerList.some(
        (reviewer) => reviewer.id === authUser?.id
      );

      setIsHaveApproval(isHaveApproval);
    }
  }, [approvalTemplates, authUser?.id, data, isSuccess]);

  useEffect(() => {
    if (id && status !== "draft" && status !== "not_approved") {
      setDisabled(true);
    }
    return () => {
      setDisabled(false);
    };
  }, [id, setDisabled, status]);

  useEffect(() => {
    if (state) {
      const { copied_unique_id, copied_id, ...otherState } = state;
      reset({
        ...otherState,
        created_date: dayjs(),
        issue_date: dayjs(),
        delivery_date: dayjs(),
      });
      setReferenceItemList(state.item_list);
    }
  }, [reset, state]);

  useEffect(() => {
    if (formatItemList && formatItemList.length > 0) {
      setReferenceItemList(formatItemList);
    }
  }, [formatItemList]);

  const { mutateAsync: create, isLoading: isCreating } =
    usePurchaseReturnCreateMutation<Error>(graphQLClient);

  const { mutateAsync: update, isLoading: isUpdating } =
    usePurchaseReturnUpdateMutation<Error>(graphQLClient);

  const draftHandler = async (data: IPurchaseReturn) => {
    if (!id) {
      try {
        const formatData = purchaseReturnCreatePayloadFormatter(data, "draft");
        const { PurchaseReturnCreate } = await create({
          input: formatData as PurchaseReturnCreateInput,
        });
        enqueueSnackbar(`${t("button.save_draft")}สำเร็จ`, {
          variant: "success",
        });
        navigate(`/purchase/return/${PurchaseReturnCreate.id}`);
        if (state && state.copied_id) {
          await createActivityLog({
            activity_type: ActivityType.Copy,
            document_type: ActivityLogDocumentType.PurchaseReturn,
            reference_id: PurchaseReturnCreate?.id,
            activity_detail: {
              copied_from: {
                id: state.copied_id,
                unique_id: state.copied_unique_id,
              },
              copied_to: {
                id: PurchaseReturnCreate.id,
                unique_id: PurchaseReturnCreate.unique_id,
              },
            },
          });
        }
        await createActivityLog({
          activity_type: ActivityType.StatusChange,
          document_type: ActivityLogDocumentType.PurchaseReturn,
          reference_id: PurchaseReturnCreate?.id,
          activity_detail: {
            secondary_operation: ActivityType.Create,
            curr_status: "draft",
            updated_fields: {
              net_amount: PurchaseReturnCreate.net_amount,
            },
          },
        });
      } catch (err) {
        const formatError = errorMessageFormatter(err);
        enqueueSnackbar(formatError || `${t("button.save_draft")}ไม่สำเร็จ`, {
          variant: "error",
        });
      }
    } else {
      try {
        const formatData = await purchaseReturnUpdatePayloadFormatter(
          data,
          "draft"
        );
        const { PurchaseReturnUpdate } = await update({
          uniqueInput: {
            id: id ? parseInt(id) : undefined,
          },
          input: formatData as PurchaseReturnUpdateInput,
        });
        enqueueSnackbar(`${t("button.save_draft")}สำเร็จ`, {
          variant: "success",
        });

        await refetch();

        await createActivityLog({
          activity_type: ActivityType.Edit,
          document_type: ActivityLogDocumentType.PurchaseReturn,
          reference_id: PurchaseReturnUpdate?.id,
          activity_detail: {
            secondary_operation: ActivityType.Edit,
            updated_fields: {
              net_amount:
                netAmount?.toFixed(2) === PurchaseReturnUpdate.net_amount?.toFixed(2)
                  ? undefined
                  : PurchaseReturnUpdate.net_amount,
            },
          },
        });
      } catch (err) {
        const formatError = errorMessageFormatter(err);
        enqueueSnackbar(formatError || `${t("button.save_draft")}ไม่สำเร็จ`, {
          variant: "error",
        });
      }
    }
  };

  const waitApproveHandler = async (data: IPurchaseReturn) => {
    if (!id) {
      try {
        const formatData = purchaseReturnCreatePayloadFormatter(
          data,
          "wait_approve",
          isApproval ? 1 : 0
        );
        const { PurchaseReturnCreate } = await create({
          input: formatData as PurchaseReturnCreateInput,
        });
        enqueueSnackbar(`${t("button.send") + t("button.approve")}สำเร็จ`, {
          variant: "success",
        });
        navigate(`/purchase/return/${PurchaseReturnCreate.id}`);
        if (state && state.copied_id) {
          await createActivityLog({
            activity_type: ActivityType.Copy,
            document_type: ActivityLogDocumentType.PurchaseReturn,
            reference_id: PurchaseReturnCreate?.id,
            activity_detail: {
              copied_from: {
                id: state.copied_id,
                unique_id: state.copied_unique_id,
              },
              copied_to: {
                id: PurchaseReturnCreate.id,
                unique_id: PurchaseReturnCreate.unique_id,
              },
            },
          });
        }
        await createActivityLog({
          activity_type: ActivityType.StatusChange,
          document_type: ActivityLogDocumentType.PurchaseReturn,
          reference_id: PurchaseReturnCreate?.id,
          activity_detail: {
            secondary_operation: ActivityType.Create,
            curr_status: "wait_approve",
            updated_fields: {
              net_amount: PurchaseReturnCreate.net_amount,
            },
          },
        });
      } catch (err) {
        const formatError = errorMessageFormatter(err);
        enqueueSnackbar(
          formatError || `${t("button.send") + t("button.approve")}ไม่สำเร็จ`,
          {
            variant: "error",
          }
        );
      }
    } else {
      try {
        const formatData = await purchaseReturnUpdatePayloadFormatter(
          data,
          "wait_approve",
          isApproval ? 1 : 0
        );
        const { PurchaseReturnUpdate } = await update({
          uniqueInput: {
            id: id ? parseInt(id) : undefined,
          },
          input: formatData as PurchaseReturnUpdateInput,
        });
        await refetch();
        const formattedDirtyFields = Object.keys(dirtyFields);
        await createActivityLog({
          activity_type: ActivityType.StatusChange,
          document_type: ActivityLogDocumentType.PurchaseReturn,
          reference_id: PurchaseReturnUpdate?.id,
          activity_detail: {
            secondary_operation:
              formattedDirtyFields?.length > 0 ? ActivityType.Edit : undefined,
            prev_status: data.aggrid_status,
            curr_status: "wait_approve",
            updated_fields: {
              net_amount:
                netAmount?.toFixed(2) === PurchaseReturnUpdate.net_amount?.toFixed(2)
                  ? undefined
                  : PurchaseReturnUpdate.net_amount,
            },
          },
        });
      } catch (err) {
        const formatError = errorMessageFormatter(err);
        enqueueSnackbar(
          formatError || `${t("button.send") + t("button.approve")}ไม่สำเร็จ`,
          {
            variant: "error",
          }
        );
      }
    }
  };

  const approveHandler = async (data: IPurchaseReturn) => {
    if (!id) {
      //guarantee no approval
      try {
        const reviewed_by = {
          user_id: authUser?.id,
          first_name: authUser?.first_name || "",
          last_name: authUser?.last_name || "",
          reviewed_date: dayjs(),
        };

        const formatData = purchaseReturnCreatePayloadFormatter(
          data,
          "approved",
          undefined,
          reviewed_by
        );

        const { PurchaseReturnCreate } = await create({
          input: formatData as PurchaseReturnCreateInput,
        });
        enqueueSnackbar(`${t("button.approve")}สำเร็จ`, {
          variant: "success",
        });
        navigate(`/purchase/return/${PurchaseReturnCreate.id}`);
        if (state && state.copied_id) {
          await createActivityLog({
            activity_type: ActivityType.Copy,
            document_type: ActivityLogDocumentType.PurchaseReturn,
            reference_id: PurchaseReturnCreate?.id,
            activity_detail: {
              copied_from: {
                id: state.copied_id,
                unique_id: state.copied_unique_id,
              },
              copied_to: {
                id: PurchaseReturnCreate.id,
                unique_id: PurchaseReturnCreate.unique_id,
              },
            },
          });
        }
        // const reference_document_list = getValues("reference_document_list");
        // if (reference_document_list && reference_document_list.length > 0) {
        //   await createActivityLog({
        //     activity_type: ActivityType.StatusChange,
        //     document_type: ActivityLogDocumentType.PurchaseOrder,
        //     reference_id: reference_document_list[0].document_id,
        //     activity_detail: {
        //       curr_status: "finished",
        //     },
        //   });
        // }
        await createActivityLog({
          activity_type: ActivityType.StatusChange,
          document_type: ActivityLogDocumentType.PurchaseReturn,
          reference_id: PurchaseReturnCreate?.id,
          activity_detail: {
            secondary_operation: ActivityType.Create,
            curr_status: "approved",
            updated_fields: {
              net_amount: PurchaseReturnCreate.net_amount,
            },
          },
        });
      } catch (err) {
        const formatError = errorMessageFormatter(err);
        enqueueSnackbar(formatError || `${t("button.approve")}ไม่สำเร็จ`, {
          variant: "error",
        });
      }
    } else {
      try {
        let changingStatus = "wait_approve";
        let currentApprovalList: IApprovalTemplate[] = [];
        if (approvalStep && approvalStep > approvalTemplates.length - 1) {
          changingStatus = "approved";
          currentApprovalList = approvalTemplates as IApprovalTemplate[];
        }

        if (!isApproval) {
          changingStatus = "approved";
        }

        let currentApprovalStep = (approvalStep || 0) + 1;

        if (data.aggrid_status === "not_approved") {
          currentApprovalStep = approvalStep || 0;
        }
        const reviewed_by = {
          user_id: authUser?.id,
          first_name: authUser?.first_name || "",
          last_name: authUser?.last_name || "",
          reviewed_date: dayjs(),
        };

        const formatData = await purchaseReturnUpdatePayloadFormatter(
          data,
          changingStatus,
          currentApprovalStep,
          currentApprovalList,
          false,
          reviewed_by
        );
        const { PurchaseReturnUpdate } = await update({
          uniqueInput: {
            id: id ? parseInt(id) : undefined,
          },
          input: formatData as PurchaseReturnUpdateInput,
        });
        enqueueSnackbar(`${t("button.approve")}สำเร็จ`, {
          variant: "success",
        });

        await refetch();

        if (isApproval) {
          if (changingStatus === "approved") {
            await createActivityLog({
              activity_type: ActivityType.StatusChange,
              document_type: ActivityLogDocumentType.PurchaseReturn,
              reference_id: PurchaseReturnUpdate?.id,
              activity_detail: {
                secondary_operation: ActivityType.Approve,
                message: [`ลำดับที่ ${approvalStep}`],
                prev_status: "wait_approve",
                curr_status: "approved",
              },
            });
          } else {
            await createActivityLog({
              activity_type: ActivityType.Approve,
              document_type: ActivityLogDocumentType.PurchaseReturn,
              reference_id: PurchaseReturnUpdate?.id,
              activity_detail: {
                message: [`ลำดับที่ ${approvalStep}`],
              },
            });
          }
        } else {
          const formattedDirtyFields = Object.keys(dirtyFields);
          await createActivityLog({
            activity_type: ActivityType.StatusChange,
            document_type: ActivityLogDocumentType.PurchaseReturn,
            reference_id: PurchaseReturnUpdate?.id,
            activity_detail: {
              secondary_operation:
                formattedDirtyFields?.length > 0
                  ? ActivityType.Edit
                  : undefined,
              prev_status: data.aggrid_status,
              curr_status: "approved",
              updated_fields:
                formattedDirtyFields?.length > 0
                  ? formattedDirtyFields
                  : undefined,
            },
          });
        }
      } catch (err) {
        const formatError = errorMessageFormatter(err);
        enqueueSnackbar(formatError || `${t("button.approve")}ไม่สำเร็จ`, {
          variant: "error",
        });
      }
    }
  };

  const notApproveHandler = async (data: IPurchaseReturn) => {
    try {
      const flag_status =
        data?.flag_status && data.flag_status.length > 0
          ? data.flag_status.includes("not_approved")
            ? [...data.flag_status]
            : [...data.flag_status, "not_approved"]
          : ["not_approved"];

      const { PurchaseReturnUpdate } = await update({
        input: {
          branch_id: data.branch_id,
          flag_status: flag_status,
        } as PurchaseReturnUpdateInput,
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
      });

      await refetch();

      const formattedDirtyFields = Object.keys(dirtyFields);

      await createActivityLog({
        activity_type: ActivityType.StatusChange,
        document_type: ActivityLogDocumentType.PurchaseReturn,
        reference_id: PurchaseReturnUpdate?.id,
        activity_detail: {
          secondary_operation:
            formattedDirtyFields?.length > 0 ? ActivityType.Edit : undefined,
          prev_status: data.aggrid_status,
          curr_status: "not_approved",
          updated_fields:
            formattedDirtyFields?.length > 0 ? formattedDirtyFields : undefined,
        },
      });

      enqueueSnackbar(`${t("button.not_approve")}สำเร็จ`, {
        variant: "success",
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(`${t("button.not_approve")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const editHandler = async (data: IPurchaseReturn, isNotApprove?: boolean) => {
    try {
      const formatData = await purchaseReturnUpdatePayloadFormatter(
        data,
        data?.main_status || "",
        approvalStep,
        approverList,
        isNotApprove
      );
      const { PurchaseReturnUpdate } = await update({
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
        input: formatData as PurchaseReturnUpdateInput,
      });

      enqueueSnackbar(`${t("sentence.edit")}สำเร็จ`, {
        variant: "success",
      });

      await refetch();

      const formattedDirtyFields = Object.keys(dirtyFields);

      await createActivityLog({
        activity_type: ActivityType.Edit,
        document_type: ActivityLogDocumentType.PurchaseReturn,
        reference_id: PurchaseReturnUpdate?.id,
        activity_detail: {
          secondary_operation:
            formattedDirtyFields?.length > 0 ? ActivityType.Edit : undefined,
          updated_fields: {
            net_amount:
              netAmount?.toFixed(2) === PurchaseReturnUpdate.net_amount?.toFixed(2)
                ? undefined
                : PurchaseReturnUpdate.net_amount,
          },
        },
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(`${t("sentence.edit")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const cancelHandler = async () => {
    try {
      const data = getValues();
      const flag_status =
        data?.flag_status && data.flag_status.length > 0
          ? data.flag_status.includes("cancelled")
            ? [...data.flag_status]
            : [...data.flag_status, "cancelled"]
          : ["cancelled"];

      const { PurchaseReturnUpdate } = await update({
        uniqueInput: {
          id: id ? parseInt(id) : undefined,
        },
        input: {
          branch_id: data.branch_id,
          flag_status: flag_status,
        } as PurchaseReturnUpdateInput,
      });

      enqueueSnackbar(`${t("button.cancel")}สำเร็จ`, {
        variant: "success",
      });

      await refetch();

      const formattedDirtyFields = Object.keys(dirtyFields);
      await createActivityLog({
        activity_type: ActivityType.StatusChange,
        document_type: ActivityLogDocumentType.PurchaseReturn,
        reference_id: PurchaseReturnUpdate?.id,
        activity_detail: {
          secondary_operation:
            formattedDirtyFields?.length > 0 ? ActivityType.Edit : undefined,
          prev_status: data.aggrid_status,
          curr_status: "cancelled",
          updated_fields:
            formattedDirtyFields?.length > 0 ? formattedDirtyFields : undefined,
        },
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(`${t("button.cancel")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const editClickHandler = () => {
    setDisabled(false);
  };

  const cancelEditHandler = () => {
    setDisabled(true);
    reset();
  };

  const renderButton = (status: string | undefined) => {
    switch (status) {
      case "draft":
        return (
          <>
            <CustomizedButton
              variant="outlined"
              title={t("button.save_draft")}
              disabled={isCreating || isUpdating}
              onClick={handleSubmit(draftHandler)}
            />
            {isApproval ? (
              <CustomizedButton
                title={t("button.send")}
                variant="contained"
                disabled={isCreating || isUpdating}
                onClick={handleSubmit(waitApproveHandler)}
              />
            ) : (
              <CustomizedButton
                title={t("button.approve")}
                variant="contained"
                disabled={isCreating || isUpdating}
                onClick={handleSubmit(approveHandler)}
              />
            )}
          </>
        );
      case "wait_approve":
        if (!disabled) {
          return (
            <>
              <CustomizedButton
                variant="outlined"
                title={t("button.cancel")}
                onClick={cancelEditHandler}
                disabled={isUpdating}
              />
              <CustomizedButton
                title={t("button.save")}
                variant="contained"
                onClick={() => {
                  handleSubmit((data) => editHandler(data))();
                  setDisabled(true);
                }}
                disabled={isUpdating}
              />
            </>
          );
        } else {
          if (isHaveApproval) {
            return (
              <>
                <CustomizedButton
                  variant="outlined"
                  title={t("button.not_approve")}
                  disabled={isUpdating}
                  onClick={handleSubmit(notApproveHandler)}
                />
                <CustomizedButton
                  title={t("button.approve")}
                  variant="contained"
                  onClick={handleSubmit(approveHandler)}
                  disabled={isUpdating}
                />
              </>
            );
          } else {
            return;
          }
        }
      case "not_approved":
        return (
          <>
            <CustomizedButton
              variant="outlined"
              title={t("button.save")}
              disabled={isUpdating}
              onClick={handleSubmit((data) => editHandler(data, true))}
            />
            <CustomizedButton
              title={t("button.send")}
              variant="contained"
              onClick={handleSubmit(waitApproveHandler)}
              disabled={isUpdating}
            />
          </>
        );
      case "accepted":
      case "cancelled":
      case "approved":
      case "finished":
        if (!disabled) {
          return (
            <>
              <CustomizedButton
                variant="outlined"
                title={t("button.cancel")}
                disabled={isUpdating}
                onClick={cancelEditHandler}
              />
              <CustomizedButton
                title={t("button.save")}
                variant="contained"
                onClick={() => {
                  handleSubmit((data) => editHandler(data))();
                  setDisabled(true);
                }}
                disabled={isUpdating}
              />
            </>
          );
        }
        return;
      default:
        return (
          <>
            <CustomizedButton
              variant="outlined"
              title={t("button.save_draft")}
              disabled={isCreating || isUpdating}
              onClick={handleSubmit(draftHandler)}
            />
            {isApproval ? (
              <CustomizedButton
                title={t("button.send")}
                variant="contained"
                disabled={isCreating || isUpdating}
                onClick={handleSubmit(waitApproveHandler)}
              />
            ) : (
              <CustomizedButton
                title={t("button.approve")}
                variant="contained"
                disabled={isCreating || isUpdating}
                onClick={handleSubmit(approveHandler)}
              />
            )}
          </>
        );
    }
  };

  if (
    isOptionLoading ||
    (id && (isLoadingData || isLoading || itemListLoading))
  ) {
    return <LoadingUI />;
  }

  return (
    <>
      <PurchaseReturnHeader
        approverList={approverList}
        approvalStep={approvalStep}
        editClickHandler={editClickHandler}
        cancelHandler={cancelHandler}
      />
      <ReturnInfo />
      <VendorInfo />
      <AddressInfo />
      <PurchaseReturnItemList
        documentType={documentType}
        referenceItemList={referenceItemList}
      />
      <PurchaseFooter documentType={documentType} />
      <BottomNavbar>
        {(!disabled || status === "wait_approve") && (
          <Stack direction="row" spacing={1} alignItems="center">
            {renderButton(status)}
          </Stack>
        )}
      </BottomNavbar>
    </>
  );
};

export default DocumentInfoTab;
